<!-- ─────────────────────────────────────────────────────────────────────── -->
<!-- Conversation List                                                     -->
<!-- Middle panel: search bar + scrollable list of conversation cards      -->
<!-- ─────────────────────────────────────────────────────────────────────── -->
<div class="conversation-list">

    <!-- ─── Search Bar + Select All (hidden in drafts, mentions and sent views) ────────── -->
    <div class="conversation-list__search" *ngIf="activeFolder !== InboxFolderType.DRAFTS && activeFolder !== InboxFolderType.MENTIONS && activeFolder !== InboxFolderType.SENT">
        <!-- Select-all checkbox -->
        <ui-checkbox
            [checked]="isAllConversationsSelected"
            [indeterminate]="isPartialSelectedAll"
            [disabled]="conversations.length === 0"
            [ariaLabel]="'common.select_all' | translate"
            (changed)="onAllConversationsToggled()">
        </ui-checkbox>

        <!-- Search input -->
        <ui-form-field
            [formControl]="searchInput"
            [prefixIcon]="'search_icon'"
            [placeholder]="'smart_inbox.inbox_view.conversation_list.search_placeholder' | translate"
            [fullWidth]="true">
        </ui-form-field>

        <!-- Advanced filter button -->
        <div class="conversation-list__filter-btn" [matMenuTriggerFor]="advancedFilterMenu" #advancedFilterTrigger="matMenuTrigger">
            <ui-button
                icon="filter-cc"
                iconSize="medium"
                [type]="hasActiveAdvancedFilter ? 'primary' : 'secondary'"
                [matTooltip]="'smart_inbox.inbox_view.conversation_list.advanced_filters_tooltip' | translate"
                matTooltipClass="conversation-tooltip-info">
            </ui-button>
        </div>

        <mat-menu #advancedFilterMenu="matMenu" class="inbox-menu" panelClass="caf-filter-panel">
            <ng-template matMenuContent>
                <app-conversation-advanced-filter
                    [currentFilter]="filter"
                    (applied)="onAdvancedFilterApply($event)"
                    (cleared)="onAdvancedFilterClear()">
                </app-conversation-advanced-filter>
            </ng-template>
        </mat-menu>
    </div>

    <!-- ─── Drafts Header ──────────────────────────────────────────── -->
    <div class="conversation-list__drafts-header" *ngIf="activeFolder === InboxFolderType.DRAFTS">
        <ui-svg-icon svgIcon="draft" size="medium"></ui-svg-icon>
        <span>{{ 'smart_inbox.inbox_view.conversation_list.drafts_header' | translate }}</span>
    </div>
    <!-- ─── Drafts List ──────────────────────────────────────────────── -->
    <div class="conversation-list__items"
         *ngIf="activeFolder === InboxFolderType.DRAFTS"
         (scroll)="onScroll($event)"
         #conversationContainer>

        <!-- Loading spinner -->
        <div class="conversation-list__sync-banner" *ngIf="isLoading">
            <ui-svg-icon svgIcon="refresh-cc" size="large" [class.spin-animation]="isLoading"></ui-svg-icon>
            <div class="no-content-text">
                <h3>{{ 'smart_inbox.inbox_view.loading_in_progress' | translate }}</h3>
            </div>
        </div>

        <p class="conversation-list__empty" *ngIf="drafts.length === 0 && !isLoading">
            {{ 'smart_inbox.inbox_view.conversation_list.no_drafts_found' | translate }}
        </p>

        <div class="conversation-list__item conversation-list__item--draft"
             *ngFor="let draft of drafts; trackBy: trackByDraftId"
             (click)="openDraft(draft)">
            <div class="conversation-list__item-info">
                <!-- Top row: compose-type tag + time -->
                <div class="conversation-list__item-header">
                    <span class="conversation-list__item-tag">
                        <ui-svg-icon [svgIcon]="getComposeTypeIcon(draft.type)" size="small"></ui-svg-icon>
                        <span class="conversation-list__item-tag-text">{{ getComposeTypeLabel(draft.type) }}</span>
                    </span>
                    <span class="conversation-list__item-date" *ngIf="draft.updated_at"
                          [matTooltip]="draft.updated_at | mailTime: true"
                          matTooltipClass="conversation-tooltip-info">
                        {{ draft.updated_at | mailTime }}
                    </span>
                </div>

                <!-- To -->
                <div class="conversation-list__item-to">
                    <span class="conversation-list__item-to-text">{{ 'smart_inbox.inbox_view.conversation_list.sent_to' | translate }} {{ draft.to && draft.to.length ? draft.to.join(', ') : ('smart_inbox.inbox_view.conversation_list.no_recipient' | translate) }}</span>
                </div>

                <!-- Subject -->
                <div class="conversation-list__item-content">
                    <h5>{{ draft.subject || ('smart_inbox.inbox_view.conversation_list.no_subject' | translate) }}</h5>
                </div>
            </div>
        </div>

        <!-- Pagination loading indicator -->
        <div class="conversation-list__loading-more" *ngIf="drafts.length !== 0 && isLoadingMore">
            <span>{{ 'smart_inbox.inbox_view.loading' | translate }}</span>
        </div>
    </div>

    <!-- ─── Mentions Header ─────────────────────────────────────────── -->
    <div class="conversation-list__mentions-header" *ngIf="activeFolder === InboxFolderType.MENTIONS">
        <ui-svg-icon svgIcon="mention" size="medium"></ui-svg-icon>
        <span>{{ 'smart_inbox.inbox_view.conversation_list.mentions_header' | translate }}</span>
    </div>
    <!-- ─── Mentions List ──────────────────────────────────────────────── -->
    <div class="conversation-list__items conversation-list__items--mentions"
         *ngIf="activeFolder === InboxFolderType.MENTIONS"
         (scroll)="onScroll($event)"
         #conversationContainer>

        <div class="conversation-list__sync-banner" *ngIf="isLoading">
            <ui-svg-icon svgIcon="refresh-cc" size="large" [class.spin-animation]="isLoading"></ui-svg-icon>
            <div class="no-content-text">
                <h3>{{ 'smart_inbox.inbox_view.loading_in_progress' | translate }}</h3>
            </div>
        </div>

        <p class="conversation-list__empty" *ngIf="mentions.length === 0 && !isLoading">
            {{ 'smart_inbox.inbox_view.conversation_list.no_mentions_found' | translate }}
        </p>

        <div class="conversation-list__item conversation-list__item--mention"
            role="option"
            [attr.aria-selected]="activeConversation?.id === mention.thread_id"
            [class.selected]="activeConversation?.id === mention.thread_id"
            [class.not-read]="!mention.is_read"
             *ngFor="let mention of mentions; trackBy: trackByMentionId"
             (click)="openMention(mention)">
            <div class="conversation-list__mention-avatar" [style.background-color]="getMentionAuthorColor(mention)">
                <span class="conversation-list__mention-initials">{{ getMentionAuthorInitials(mention) }}</span>
            </div>
            <div class="conversation-list__mention-body">
                <div class="conversation-list__mention-row conversation-list__mention-row--first">
                    <span class="conversation-list__mention-description" [innerHTML]="getMentionDescription(mention)"></span>
                    <span class="conversation-list__mention-date"
                          [matTooltip]="mention.created_at | mailTime: true"
                          matTooltipClass="conversation-tooltip-info">{{ mention.created_at | mailTime }}</span>
                </div>
                <div class="conversation-list__mention-row">
                    <span class="conversation-list__mention-context">{{ 'smart_inbox.inbox_view.conversation_list.mention_in' | translate }}</span><strong>{{ mention.subject }}</strong>
                </div>
                <div class="conversation-list__mention-row conversation-list__mention-comment">
                    <span class="conversation-list__mention-comment-text">{{ mention.comment }}</span>
                </div>
            </div>
        </div>

        <div class="conversation-list__loading-more" *ngIf="mentions.length !== 0 && isLoadingMore">
            <span>{{ 'smart_inbox.inbox_view.loading' | translate }}</span>
        </div>
    </div>

    <!-- ─── Sent Header ──────────────────────────────────────────── -->
    <div class="conversation-list__drafts-header" *ngIf="activeFolder === InboxFolderType.SENT">
        <ui-svg-icon svgIcon="send-cc" size="medium"></ui-svg-icon>
        <span>{{ 'smart_inbox.inbox_view.conversation_list.sent_header' | translate }}</span>
    </div>
    <!-- ─── Sent List (flat list of sent messages, not threads) ────────── -->
    <div class="conversation-list__items"
         *ngIf="activeFolder === InboxFolderType.SENT"
         (scroll)="onScroll($event)"
         #conversationContainer>

        <!-- Loading spinner -->
        <div class="conversation-list__sync-banner" *ngIf="isLoading">
            <ui-svg-icon svgIcon="refresh-cc" size="large" [class.spin-animation]="isLoading"></ui-svg-icon>
            <div class="no-content-text">
                <h3>{{ 'smart_inbox.inbox_view.loading_in_progress' | translate }}</h3>
            </div>
        </div>

        <p class="conversation-list__empty" *ngIf="sentMessages.length === 0 && !isLoading">
            {{ 'smart_inbox.inbox_view.conversation_list.no_sent_found' | translate }}
        </p>

        <div class="conversation-list__item conversation-list__item--sent"
             role="option"
             [attr.aria-selected]="activeConversation?.id === message.thread_id"
             [class.selected]="activeConversation?.id === message.thread_id"
             *ngFor="let message of sentMessages; trackBy: trackBySentMessageId"
             (click)="openSentMessage(message)">
            <div class="conversation-list__item-info">
                <!-- Top row: account tag + time -->
                <div class="conversation-list__item-header">
                    <!-- Same indicator as the conversation cards, replacing the
                         name-in-a-grey-chip this list used to have on its own: one
                         mailbox is one tag everywhere in Smart Inbox. -->
                    <ui-initials-tag
                        *ngIf="sentMailboxTagFor(message) as mailboxTag"
                        size="small"
                        [label]="mailboxTag.label"
                        [ariaLabel]="mailboxTag.ariaLabel"
                        [color]="mailboxTag.bg"
                        [borderColor]="mailboxTag.border"
                        [textColor]="mailboxTag.text"
                        [matTooltip]="mailboxTag.tooltip"
                        matTooltipClass="inbox-account-tooltip"
                        matTooltipPosition="above">
                    </ui-initials-tag>
                    <span class="conversation-list__item-date" *ngIf="message.timestamp"
                          [matTooltip]="message.timestamp | mailTime: true"
                          matTooltipClass="conversation-tooltip-info">
                        {{ message.timestamp | mailTime }}
                    </span>
                </div>

                <!-- To -->
                <div class="conversation-list__item-to">
                    <span class="conversation-list__item-to-text">{{ 'smart_inbox.inbox_view.conversation_list.sent_to' | translate }} {{ message.recipients || ('smart_inbox.inbox_view.conversation_list.no_recipient' | translate) }}</span>
                </div>

                <!-- Subject -->
                <div class="conversation-list__item-content">
                    <h5>{{ message.subject || ('smart_inbox.inbox_view.conversation_list.no_subject' | translate) }}</h5>
                </div>
            </div>
        </div>

        <!-- Pagination loading indicator -->
        <div class="conversation-list__loading-more" *ngIf="sentMessages.length !== 0 && isLoadingMore">
            <span>{{ 'smart_inbox.inbox_view.loading' | translate }}</span>
        </div>
    </div>

    <!-- ─── Scrollable Conversation List ────────────────────────────── -->
    <div class="conversation-list__items"
         *ngIf="activeFolder !== InboxFolderType.DRAFTS && activeFolder !== InboxFolderType.MENTIONS && activeFolder !== InboxFolderType.SENT && activeAccounts.length > 0"
         tabindex="0"
         role="listbox"
         [attr.aria-label]="'smart_inbox.inbox_view.conversation_list.aria_list' | translate"
         dragToScroll
         dragScrollAxis="y"
         [dragScrollEnabled]="conversations.length > 0"
         (scroll)="onScroll($event)"
         (keydown)="onConversationListKeydown($event)"
         #conversationContainer>

        <!-- ── Mailbox Syncing Banner (real `sync_status`, not GET loading) ── -->
        <div class="conversation-list__sync-banner" *ngIf="isSyncing">
            <ui-svg-icon
                svgIcon="refresh-cc"
                size="large"
                class="spin-animation">
            </ui-svg-icon>
            <div class="no-content-text">
                <h3>{{ 'smart_inbox.inbox_view.initial_sync.title' | translate }}</h3>
                <h5>{{ 'smart_inbox.inbox_view.initial_sync.subtitle' | translate }}</h5>
            </div>
        </div>

        <!-- ── Mailbox Sync Failed Banner ──────────────────────────── -->
        <div class="conversation-list__sync-banner conversation-list__sync-banner--error" *ngIf="hasSyncError">
            <ui-svg-icon svgIcon="alert-circle-red" size="large"></ui-svg-icon>
            <div class="no-content-text">
                <h3>{{ 'smart_inbox.inbox_view.sync_error.title' | translate:{ mailboxes: syncStateEmails } }}</h3>
                <h5>{{ syncState.error || ('smart_inbox.inbox_view.sync_error.subtitle' | translate) }}</h5>
            </div>
        </div>

        <!-- Loading spinner — a `/threads` fetch, deliberately its own banner -->
        <div class="conversation-list__sync-banner"
             *ngIf="isLoading">
            <ui-svg-icon
                svgIcon="refresh-cc"
                size="large"
                class="spin-animation">
            </ui-svg-icon>
            <div class="no-content-text">
                <h3>{{ 'smart_inbox.inbox_view.loading_in_progress' | translate }}</h3>
            </div>
        </div>

        <!-- ── No Emails Found (+ a "sync now" that really syncs) ───── -->
        <ng-container *ngIf="conversations.length === 0 && !isLoading && !isSyncRequestBlocked">
            <p class="conversation-list__empty" data-testid="conversation-list-empty">
                {{ (isFilterApplied ? 'smart_inbox.inbox_view.no_emails_found_search_applied' : 'smart_inbox.inbox_view.no_emails_found') | translate }}
            </p>
            <div class="conversation-list__empty-action"
                 *ngIf="!isFilterApplied && !isSharedInboxOnlyActive()">
                <ui-button
                    type="secondary"
                    icon="refresh-cc"
                    iconPosition="start"
                    [disabled]="isSyncRequestBlocked"
                    [label]="'smart_inbox.inbox_view.sync_now' | translate"
                    (click)="onSyncRequested()">
                </ui-button>
            </div>
        </ng-container>

        <!-- ── Conversation Cards ────────────────────────────────────── -->
        <div class="conversation-list__item"
             #conversationRow
             [attr.data-testid]="'conversation-row-' + conversation.id"
             role="option"
             [attr.aria-selected]="activeConversation?.id === conversation.id"
             [class.selected]="activeConversation?.id === conversation.id"
             [class.not-read]="!conversation.is_read"
             *ngFor="let conversation of conversations; trackBy: trackByConversationId"
             (click)="onConversationSelected(conversation)">
            <div class="conversation-list__item-info">

                <!-- Top row: checkbox + sender + date -->
                <div class="conversation-list__item-header">
                    <ui-checkbox
                        [checked]="selectedConversationIds.includes(conversation.id)"
                        [ariaLabel]="'common.select' | translate"
                        (changed)="onConversationToggled(conversation, $event.checked, $event)">
                    </ui-checkbox>
                    <h3 class="conversation-list__item-sender" [innerHTML]="(conversation.sender || 'Unknown') | highlight: filter.searchTerm"></h3>
                    <ui-svg-icon *ngIf="conversation.is_urgent"
                                 class="conversation-list__item-urgent"
                                 svgIcon="flag-filled"
                                 size="small"
                                 [matTooltip]="'smart_inbox.inbox_view.detail.urgency_urgent' | translate"
                                 matTooltipClass="conversation-tooltip-warning"
                                 matTooltipPosition="above">
                    </ui-svg-icon>
                    <span class="conversation-list__item-date"
                          [matTooltip]="conversation.last_message_at | mailTime: true"
                          matTooltipClass="conversation-tooltip-info">
                        {{ conversation.last_message_at | mailTime }}
                    </span>
                </div>

                <!-- Subject -->
                <div class="conversation-list__item-content">
                    <h5 [innerHTML]="conversation.subject | highlight: filter.searchTerm"></h5>
                </div>

                <!-- Meta row: category tag (+n) | attachment + assignee + status -->
                <div class="conversation-list__item-footer">
                    <!-- Grouped so the footer keeps three flex children: as a fourth
                         direct child, the tag changed how space-between distributed
                         free space, drifting the category tags toward the centre
                         depending on how many mailboxes the user had connected. -->
                    <div class="conversation-list__item-tags">
                    <!-- Which mailbox received the thread. Hidden while a single
                         mailbox is in view, since it would say the same thing
                         on every row. -->
                    <ui-initials-tag
                        *ngIf="mailboxTagFor(conversation) as mailboxTag"
                        size="small"
                        [label]="mailboxTag.label"
                        [ariaLabel]="mailboxTag.ariaLabel"
                        [color]="mailboxTag.bg"
                        [borderColor]="mailboxTag.border"
                        [textColor]="mailboxTag.text"
                        [matTooltip]="mailboxTag.tooltip"
                        matTooltipClass="inbox-account-tooltip"
                        matTooltipPosition="above">
                    </ui-initials-tag>

                    <!-- Only the primary category is shown on the card; the rest
                         collapse into a "+n" chip listing them in its tooltip. -->
                    <ui-ai-classification-tag
                        *ngIf="getPrimaryCategory(conversation) as primaryCategory"
                        size="small"
                        [tone]="primaryCategory"
                        [label]="classificationLabelKey(primaryCategory) | translate">
                    </ui-ai-classification-tag>

                    <ui-ai-classification-tag
                        *ngIf="getExtraCategoryCount(conversation) as extraCount"
                        size="small"
                        variant="muted"
                        [label]="'+' + extraCount"
                        [tooltip]="getExtraCategoriesLabel(conversation)">
                    </ui-ai-classification-tag>
                    </div>

                    <div class="conversation-list__item-meta">
                        <div class="conversation-list__attachment-icon"
                             [matTooltip]="'smart_inbox.inbox_view.conversation_list.attachments_count' | translate:{ count: conversation.attachment_count }"
                             matTooltipClass="conversation-tooltip-info"
                             matTooltipPosition="above"
                             *ngIf="conversation.attachment_count > 0">
                            <ui-svg-icon svgIcon="thread-attachment" size="small"></ui-svg-icon>
                            {{ conversation.attachment_count }}
                        </div>

                        <span class="conversation-list__item-avatar"
                              *ngIf="conversation.assignee as assignee"
                              [style.background-color]="getUserColor(assignee)"
                              [matTooltip]="assignee.name"
                              matTooltipClass="conversation-tooltip-info"
                              matTooltipPosition="above">
                            {{ getUserInitials(assignee) }}
                        </span>

                        <span class="conversation-tag"
                              [class]="'status-' + (conversation.status || 'new')"
                              *ngIf="conversation.status">
                            {{ ('smart_inbox.inbox_view.detail.status_' + conversation.status) | translate }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Pagination Loading Indicator ────────────────────────── -->
        <div class="conversation-list__loading-more" *ngIf="conversations.length !== 0 && isLoadingMore">
            <span>{{ 'smart_inbox.inbox_view.loading' | translate }}</span>
        </div>
    </div>
</div>
